import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Tag } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { fetchEnumGroups, createEnumGroup } from "../../lib/api/products";
import type { EnumGroup } from "../../lib/api/products";
import * as pageStyles from "../organizations/OrganizationDetailPage.css";

export function EnumGroupsListPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [groups, setGroups] = React.useState<EnumGroup[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createName, setCreateName] = React.useState("");
  const [createDescription, setCreateDescription] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const load = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetchEnumGroups(token)
      .then(setGroups)
      .catch((err) => setError(err?.message || t("pro.system.enums.error.load")))
      .finally(() => setLoading(false));
  }, [token, t]);

  React.useEffect(() => { load(); }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !createName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createEnumGroup(token, {
        name: createName.trim(),
        description: createDescription.trim() || null,
      });
      setCreateOpen(false);
      setCreateName("");
      setCreateDescription("");
      navigate({ to: "/system/data-catalog/enum-groups/$groupId", params: { groupId: created.id } });
    } catch (err: any) {
      setCreateError(err?.message || t("pro.system.enums.error.create"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={pageStyles.page}>
      <div style={{ width: "100%", maxWidth: 960 }}>
        <button
          type="button"
          onClick={() => navigate({ to: "/system/data-catalog" })}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "#FFCF51",
            background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 16 }}
        >
          <ArrowLeft size={14} /> {t("pro.system.dataCatalog.title")}
        </button>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <Text level="h1" weight="bold" style={{ marginBottom: 4 }}>{t("pro.system.enums.title")}</Text>
            <Text level="body-medium" style={{ color: "#6B7280" }}>
              {t("pro.system.enums.subtitle")}
            </Text>
          </div>
          <Button type="button" color="primary" onClick={() => setCreateOpen(true)}>
            {t("pro.system.enums.newGroup")}
          </Button>
        </div>

        {error && <Callout variant="error">{error}</Callout>}
        {loading && <Text level="body-medium" style={{ color: "#9CA3AF" }}>{t("shared.common.loading")}</Text>}

        {!loading && groups.length === 0 && !error && (
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>
            {t("pro.system.enums.empty")}
          </Text>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {groups.map((group) => (
            <Card key={group.id} maxWidth={960} style={{ width: "100%", cursor: "pointer" }}>
              <button
                type="button"
                onClick={() => navigate({ to: "/system/data-catalog/enum-groups/$groupId", params: { groupId: group.id } })}
                style={{ width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Tag size={14} style={{ color: "#6B7280", flexShrink: 0 }} />
                      <Text level="h3" weight="bold">{group.name}</Text>
                      {!group.isActive && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 9999,
                          background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB" }}>
                          {t("pro.system.enums.badge.inactive")}
                        </span>
                      )}
                    </div>
                    {group.description && (
                      <Text level="body-small" style={{ color: "#6B7280", marginTop: 2 }}>
                        {group.description}
                      </Text>
                    )}
                    {group.valueCount != null && (
                      <Text level="body-small" style={{ color: "#9CA3AF", marginTop: 2 }}>
                        {t("pro.system.enums.codeCount", { count: group.valueCount })}
                      </Text>
                    )}
                  </div>
                  <span style={{ color: "#9CA3AF", fontSize: 18, flexShrink: 0, marginLeft: 12 }}>›</span>
                </div>
              </button>
            </Card>
          ))}
        </div>
      </div>

      <Modal open={createOpen} setOpen={setCreateOpen} minWidth="420px" maxWidth="500px" closeOnClickOff>
        <div style={{ padding: "24px 28px" }}>
          <Text level="h2" weight="bold" style={{ marginBottom: 20 }}>{t("pro.system.enums.create.title")}</Text>
          <form onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <TextInput label={t("pro.system.enums.create.nameRequired")} value={createName} onChange={(e) => setCreateName(e.target.value)}
                placeholder={t("pro.system.enums.create.namePlaceholder")} disabled={creating} autoFocus />
              <TextInput label={t("pro.system.enums.create.description")} value={createDescription} onChange={(e) => setCreateDescription(e.target.value)}
                placeholder={t("pro.system.enums.create.descriptionPlaceholder")} disabled={creating} />
            </div>
            {createError && <div style={{ marginTop: 14 }}><Callout variant="error">{createError}</Callout></div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
              <Button type="button" color="neutral" onClick={() => setCreateOpen(false)} disabled={creating}>{t("shared.common.cancel")}</Button>
              <Button type="submit" color="primary" disabled={creating || !createName.trim()}>
                {creating ? t("pro.system.enums.create.creating") : t("pro.system.enums.create.create")}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
